import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { Checkbox } from "../components/ui/checkbox"
import { Label } from "../components/ui/label"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"

/** Extract last segment from a bind path. E.g., "#{form.agree}" -> "agree" */
function extractFieldName(bind?: string): string | undefined {
  if (!bind) return undefined
  const stripped = bind.replace(/^#\{/, "").replace(/\}$/, "")
  const segments = stripped.split(".")
  return segments[segments.length - 1]
}

export function CheckboxRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  // NOTE: element.variant === "checked" is a legacy initial-state fallback
  // for `fallbackChecked` below; the two code paths do not conflict because
  // the data-variant attr carries the theme-variant semantic (e.g. "default",
  // "outline") while `variant === "checked"` is a pre-v2 state marker that
  // gets coerced to "checked" in data-variant and falls back to fallbackChecked.
  const atomDefaults = useAtomDefaults("checkbox")
  const resolvedVariant = (element as { variant?: string }).variant
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const text = element.text ? String(resolveSymbol(element.text) ?? "") : undefined
  // React-standard props: defaultChecked (bool) or legacy variant/visible fallback
  const fallbackChecked =
    (element as any).defaultChecked === true ||
    (element as any).checked === true ||
    element.variant === "checked" ||
    resolveSymbol(String(element.visible ?? "")) === true

  const [checked, setChecked] = useBoundValue<boolean>(
    element.bind,
    resolveSymbol,
    fallbackChecked,
    Boolean,
  )

  const handleCheckedChange = (value: boolean) => {
    setChecked(value)
    dispatchEvent(element.id, "change", { value })
  }

  // No label: return Checkbox directly (matches shadcn DOM)
  if (!text) {
    return (
      <Checkbox
        id={element.id}
        checked={checked}
        onCheckedChange={handleCheckedChange}
        className={cn(stripPresetConflicts(element.className))}
        data-testid={`checkbox-${element.id}`}
        data-action="toggle"
        data-field-name={extractFieldName(element.bind)}
        data-field-type="checkbox"
        data-atom-type="checkbox"
        data-variant={resolvedVariant}
        data-size={resolvedSize}
      />
    )
  }
  return (
    <div className={cn("flex items-center space-x-2", stripPresetConflicts(element.className))} data-testid={`checkbox-${element.id}`} data-action="toggle" data-field-name={extractFieldName(element.bind)} data-field-type="checkbox" data-atom-type="checkbox" data-variant={resolvedVariant} data-size={resolvedSize}>
      <Checkbox id={element.id} checked={checked} onCheckedChange={handleCheckedChange} />
      <Label htmlFor={element.id}>{text}</Label>
    </div>
  )
}
